import axios from "axios";
import {
  IdeaCustomization,
  IdeaCustomizationCreateParams,
  IdeaCustomizationUpdateParams,
  IdeaPerspectivesUpdateParams,
  IdeaFiltersUpdateParams,
  IdeaColumnsUpdateParams,
  IdeaInfoTabsUpdateParams,
  IdeaPerspective,
  IdeaFilter,
  IdeaColumn,
  IdeaInfoTab,
  Customization,
  UpdateFiltersFromModalParams
} from '@bageltechnologies/bagel-common';

// Additional interfaces not available in bagel-common
export interface AvailablePerspective {
  key: string;
  label: string;
}

// Interfaces
export interface Perspective {
  label: string;
  key: string;
  countType: string;
  fieldPath?: string;
}

export interface FilterOperation {
  name: string;
  label: string;
}

export interface Filter {
  name: string;
  label: string;
  type: 'number' | 'checkbox' | 'date' | 'boolean' | 'userIdIn' | 'fetchableCheckbox' | 'parent';
  filterObject: 'idea' | 'evidence';
  operations?: FilterOperation[];
  dbFieldPath: string;
  // Additional optional properties can be added here
  [key: string]: any;
}

const API_DOMAIN = window.__RUNTIME_CONFIG__.REACT_APP_API_DOMAIN;
const API_BASE_URL = `${API_DOMAIN}/customizations-config`;

// Main Customization Operations
export const getCustomizations = async (auth0: any) => {
  try {
    const response = await axios.get(`${API_BASE_URL}`);
    return response.data;
  } catch (e) {
    console.error("Error fetching customizations", e);
    throw e;
  }
};

export const getCustomization = async (auth0: any) => {
  try {
    const response = await axios.get(`${API_BASE_URL}/idea`);
    return response.data;
  } catch (e) {
    console.error("Error fetching customization", e);
    throw e;
  }
};

export const createCustomization = async (customization: IdeaCustomizationCreateParams, auth0: any) => {
  try {
    const response = await axios.post(`${API_BASE_URL}`, customization);
    return response.data;
  } catch (e) {
    console.error("Error creating customization", e);
    throw e;
  }
};

export const updateCustomization = async (customization: IdeaCustomizationUpdateParams, auth0: any) => {
  try {
    const response = await axios.put(`${API_BASE_URL}/idea`, customization);
    return response.data;
  } catch (e) {
    console.error("Error updating customization", e);
    throw e;
  }
};

export const deleteCustomization = async (auth0: any) => {
  try {
    const response = await axios.delete(`${API_BASE_URL}/idea`);
    return response.data;
  } catch (e) {
    console.error("Error deleting customization", e);
    throw e;
  }
};

// Section-Specific Operations

// Perspectives
export const getAvailablePerspectives = async (auth0: any) => {
  try {
    const response = await axios.get(`${API_BASE_URL}/idea/available-perspectives`);
    return response.data;
  } catch (e) {
    console.error("Error fetching available perspectives", e);
    throw e;
  }
};

export const getPerspectives = async (auth0: any) => {
  try {
    const response = await axios.get(`${API_BASE_URL}/idea/perspectives`);
    return response.data;
  } catch (e) {
    console.error("Error fetching perspectives", e);
    throw e;
  }
};

export const updatePerspectives = async (data: IdeaPerspectivesUpdateParams, auth0: any) => {
  try {
    const response = await axios.put(`${API_BASE_URL}/idea/perspectives`, data);
    return response.data;
  } catch (e) {
    console.error("Error updating perspectives", e);
    throw e;
  }
};

export const deletePerspective = async (perspectiveKey: string, auth0: any) => {
  try {
    const response = await axios.delete(`${API_BASE_URL}/idea/perspectives/${perspectiveKey}`);
    return response.data;
  } catch (e) {
    console.error("Error deleting perspective", e);
    throw e;
  }
};

// Filters
export const getFilters = async (auth0: any) => {
  try {
    const response = await axios.get(`${API_BASE_URL}/idea/filters`);
    return response.data;
  } catch (e) {
    console.error("Error fetching filters", e);
    throw e;
  }
};

export const updateFilters = async (data: IdeaFiltersUpdateParams, auth0: any) => {
  try {
    const response = await axios.put(`${API_BASE_URL}/idea/filters`, data);
    return response.data;
  } catch (e) {
    console.error("Error updating filters", e);
    throw e;
  }
};

export const updateFilter = async (data: any, auth0: any) => {
  try {

    const response = await axios.put(`${API_BASE_URL}/idea/filter`, data);
    return response.data;
  } catch (e) {
    console.error("Error updating filters", e);
    throw e;
  }
};

// Idea Columns
export const getIdeaColumns = async (auth0: any) => {
  try {
    const response = await axios.get(`${API_BASE_URL}/idea/columns`);
    return response.data;
  } catch (e) {
    console.error("Error fetching idea columns", e);
    throw e;
  }
};

export const updateIdeaColumns = async (data: IdeaColumnsUpdateParams, auth0: any) => {
  try {
    const response = await axios.put(`${API_BASE_URL}/idea/columns`, data);
    return response.data;
  } catch (e) {
    console.error("Error updating idea columns", e);
    throw e;
  }
};

// Info Tabs
export const getInfoTabs = async (auth0: any) => {
  try {
    const response = await axios.get(`${API_BASE_URL}/idea/info-tabs`);
    return response.data;
  } catch (e) {
    console.error("Error fetching info tabs", e);
    throw e;
  }
};

export const updateInfoTabs = async (data: IdeaInfoTabsUpdateParams, auth0: any) => {
  try {
    const response = await axios.put(`${API_BASE_URL}/idea/info-tabs`, data);
    return response.data;
  } catch (e) {
    console.error("Error updating info tabs", e);
    throw e;
  }
};

export const getFilterConfig = async (auth0: any) => {
  try {
    const response = await axios.get(`${API_BASE_URL}/idea/filter-config`);
    return response.data;
  } catch (e) {
    console.error("Error fetching filter config", e);
    throw e;
  }
};

// CSV Download Configuration
export interface CsvColumn {
  id: string;
  label: string;
  fieldPath: string;
  source: 'idea' | 'evidence';
  enabled: boolean;
  locked: boolean;
  order: number;
}

export interface CsvDownloadConfig {
  columns: CsvColumn[];
}

export interface AvailableCsvField {
  fieldPath: string;
  readableTitle: string;
  source: 'idea' | 'evidence';
  dataType: string;
}

export const getCsvDownloadConfig = async (auth0: any): Promise<{ success: boolean; data: CsvDownloadConfig }> => {
  try {
    const response = await axios.get(`${API_BASE_URL}/csv-download/config`);
    return response.data;
  } catch (e) {
    console.error("Error fetching CSV download config", e);
    throw e;
  }
};

export const updateCsvDownloadConfig = async (columns: CsvColumn[], auth0: any): Promise<{ success: boolean; data: CsvDownloadConfig }> => {
  try {
    const response = await axios.post(`${API_BASE_URL}/csv-download/config`, { columns });
    return response.data;
  } catch (e) {
    console.error("Error updating CSV download config", e);
    throw e;
  }
};

export const getAvailableCsvFields = async (auth0: any): Promise<{ success: boolean; data: AvailableCsvField[] }> => {
  try {
    const response = await axios.get(`${API_BASE_URL}/csv-download/available-fields`);
    return response.data;
  } catch (e) {
    console.error("Error fetching available CSV fields", e);
    throw e;
  }
};

export const introspectCsvFields = async (auth0: any): Promise<{ success: boolean; data: AvailableCsvField[]; message: string }> => {
  try {
    const response = await axios.post(`${API_BASE_URL}/csv-download/introspect-fields`, {});
    return response.data;
  } catch (e) {
    console.error("Error introspecting CSV fields", e);
    throw e;
  }
};


export const updateFiltersFromModal = async (data: UpdateFiltersFromModalParams, auth0: any) => {
  try {
    const response = await axios.put(`${API_BASE_URL}/idea/filtersUpdate`, data);
    return response.data;
  } catch (e) {
    console.error("Error updating filters", e);
    throw e;
  }
};